import { useSuspenseQuery } from '@tanstack/react-query';
import { createServerFn } from '@tanstack/react-start';
import { useRouteContext } from '@tanstack/react-router';
import { API } from '~/api';
import { useUserInfo } from '~/services/auth';
import { Practitioner } from '@repo/types';
import { urls } from '@repo/app-constants';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

// This is for practitioner users to get their own practitioner settings/account info,
// not for mainaccounts to get their practitioner information
const getPractitioner = createServerFn({
  method: 'GET',
  response: 'data',
})
  .validator((id: string) => id)
  .handler(async ({ data }) => {
    try {
      const response = await API.get(
        `${urls.apiBasePath}/practitioners/${data}`,
        {},
        true,
        undefined,
        {
          tags: {
            section: SECTIONS.SETTINGS,
            domain: DOMAINS.PRACTITIONERS,
            importance: IMPORTANCE.HIGH,
          },
          context: {
            practitionerId: data,
          },
        },
      );
      return response?.result;
    } catch (error) {
      console.error('[usePractitioner] API error:', error);
      throw error;
    }
  });

export const practitionerQueryFn = (id: string, isPractitioner: boolean) => {
  return {
    queryKey: ['practitioner', id],
    queryFn: async () => {
      if (id && isPractitioner) {
        return await getPractitioner({ data: id });
      }
      return {};
    },
  };
};

export const usePractitioner = () => {
  const { user, isPractitioner } = useUserInfo();
  const { isImpersonating, impersonationId } = useRouteContext({
    from: '__root__',
  });

  // Use impersonationId when impersonating, otherwise use user.id
  const userId = isImpersonating ? impersonationId : user?.id;
  const { data: practitioner, isSuccess } = useSuspenseQuery<Practitioner>(
    practitionerQueryFn(userId ?? '', isPractitioner ?? false),
  );

  const fullAddress =
    practitioner?.practice_address &&
    practitioner?.practice_address +
      ', ' +
      practitioner?.practice_city +
      ', ' +
      practitioner?.practice_state +
      ' ' +
      practitioner?.practice_zip;

  return {
    practitioner: {
      ...practitioner,
      fullAddress,
      fullName: practitioner?.first_name + ' ' + practitioner?.last_name,
    },
    isSuccess,
  };
};

export default usePractitioner;
